import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classNames from 'classnames';
import { Circle } from 'react-feather';
import { CirclePlusIcon, TickIcon, IssueIcon, ProgressIcon } from 'images/common';
import { FormattedMessage } from 'react-intl';
import { getInitials, isArrayEqual } from 'utils/common';
import NotFound from 'containers/NotFoundPage';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { compose } from 'redux';

import messages from 'utils/messages';
import { makeSelectMe } from 'containers/Settings/User/selectors';
import TableHead from './TableHead';
import TableSubHead from './TableSubHead'
import { Scrollbars } from 'react-custom-scrollbars';
import { VersionIcon, VersionUpdateIcon } from '../../images/common';
import { getColumnState, saveColumnState } from 'utils/tableStorage';


const nodeIcon = 'fas fa-paper-plane';

const objectIcon = {
  'region-of-interest': 'fas fa-regular fa-bookmark',
  'detection-tracking': 'fas fa-regular fa-bookmark',
  'roi': 'fas fa-regular fa-bookmark',
  'video-processing': 'fas fa-regular fa-bookmark',
  'video-feed': 'fas fa-video-camera',
  'video-source': 'fas fa-video-camera',
  'abandoned-detection': 'fas fa-binoculars',
  'fish-eye': 'fas fa-paper-plane',
  'multi-object-detection': 'fas fa-object-group;',
  'object-recognition': 'fas fa-object-group;',
  'object-tracking': 'fas fa-object-group;',
  'posture-recognition': 'fas fa-object-group;',
  'group-keypoint-detection': 'fas fa-object-group;',
  'image-classification': 'fas fa-object-group;',
  'reid-object-tracking': 'fas fa-object-group;',
  'face-detection': 'fas fa-user-circle',
  'deepface': 'fas fa-user-circle',
  'face-analysis': 'fas fa-user-circle',
  'face-recognition': 'fas fa-user-circle',
  'video-view': 'fas fa-desktop',
  'frame-viewer': 'fas fa-desktop',
  'data-collection': 'fas fa-registered',
  '3d-keypoint-detection': 'fas fa-user',
  'fall-detection': 'fas fa-user',
  'object-count': 'fas fa-user',
  'reboot': 'fas fa-user',
  'object-flow': 'fas fa-shopping-basket',
  'optical-flow': 'fas fa-shopping-basket',
  'barcode decoder': 'fas fa-barcode',
  'barcode generator': 'fas fa-barcode',
  'delivery-van': 'fas fa-truck',
  'alpr': 'fas fa-car',
  'color-recognition': 'fas fa-adjust',
  'image-feed': 'fas fa-image',
  'object-segmentation': 'fas fa-cubes',
  'snapshot': 'fas fa-file-video-o'
}

class Table extends Component {
  constructor(props) {
    super(props);

    // Load saved column state from localStorage if tableId is provided
    let initialColumns = props.columns.map((column) => {
      if (column.isOptional) {
        return { ...column, isVisible: false };
      }

      return { ...column, isVisible: true };
    });

    // Apply saved column state if available
    if (props.tableId) {
      const savedColumnState = getColumnState(props.tableId);

      if (savedColumnState && Array.isArray(savedColumnState)) {
        // Restore columns in the saved order
        const orderedColumns = [];
        const columnsMap = new Map(initialColumns.map(col => [col.sortKey, col]));
        
        // First, add columns in the saved order
        savedColumnState.forEach((savedCol) => {
          const column = columnsMap.get(savedCol.sortKey);
          if (column) {
            orderedColumns.push({ ...column, isVisible: savedCol.isVisible });
            columnsMap.delete(savedCol.sortKey);
          }
        });
        
        // Then add any new columns that weren't in the saved state
        columnsMap.forEach((column) => {
          orderedColumns.push(column);
        });
        
        initialColumns = orderedColumns;
      }
    }

    this.state = {
      columns: initialColumns,
      columns1: this.props.columns1,
      settingSelected: null,
      isFirstTime: true,
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleWindowSizeChange);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleWindowSizeChange);
  }

  handleWindowSizeChange = () => {
    if (window.innerWidth <= 1200) {
      const initialColumns = this.props.columns
        .map((column, index) => {
          if (column.isOptional) {
            return { ...column, isVisible: false };
          }

          if (index < 4) {
            return { ...column, isVisible: true };
          }
          return { ...column, isVisible: false };
        });

      this.onColumnsChange(initialColumns);
    }

    if (window.innerWidth <= 1050) {
      const initialColumns = this.props.columns
        .map((column, index) => {
          if (column.isOptional) {
            return { ...column, isVisible: false };
          }

          if (index < 2) {
            return { ...column, isVisible: true };
          }
          return { ...column, isVisible: false };
        });

      this.onColumnsChange(initialColumns);
    }

    if (window.innerWidth > 1200) {
      const initialColumns = this.props.columns.map((column) => {
        if (column.isOptional) {
          return { ...column, isVisible: false };
        }

        return { ...column, isVisible: true };
      });

      this.onColumnsChange(initialColumns);
    }
  };

  // eslint-disable-next-line
  componentWillReceiveProps(nextProps) {
    if (!isArrayEqual(this.props.columns, nextProps.columns)) {
      let initialColumns = nextProps.columns.map((column) => {
        if (column.isOptional) {
          return { ...column, isVisible: false };
        }

        return { ...column, isVisible: true };
      });

      // Apply saved column state if available
      if (this.props.tableId) {
        const savedColumnState = getColumnState(this.props.tableId);
        if (savedColumnState && Array.isArray(savedColumnState)) {
          initialColumns = initialColumns.map((column) => {
            const savedColumn = savedColumnState.find(
              (saved) => saved.sortKey === column.sortKey
            );
            if (savedColumn) {
              return { ...column, isVisible: savedColumn.isVisible };
            }
            return column;
          });
        }
      }

      this.setState({
        columns: initialColumns,
        settingSelected: null,
      });
    }
  }

  onSelectSetting = (id, e) => {
    this.setState({ isFirstTime: false }, () => {
      this.setState({ settingSelected: id });
      this.selectAll(e, 'clearAll')
    });
  };

  onClearSelectedSetting = () => {
    this.setState({ isFirstTime: false }, () => {
      this.setState({ settingSelected: null });
    });
  }

  renderRows() {
    const {
      rows,
      rowKey,
      disabled,
      inputType,
      rowClassName,
      hideSingleMenu,
      rowClick,
      hasImg,
      isChecked,
      multipleSelect,
      isSelectable,
      preventRedirectOnClick,
      RowComponent,
      onPushProfile,
      onShowDeleteModal,
      onAssignProfile,
      isUserSetting,
      isLogoSetting,
      isPriceSetting,
      monthlyPriceId,
      yearlyPriceId,
      isRolePage,
      isNodeType,
      selectedItems = [],
    } = this.props;
    const { isFirstTime } = this.state;

    const safeRows = (rows || []).filter(row => row && typeof row === 'object');

    return safeRows
      .sort((row1, row2) => {
        if (!isFirstTime) return 0;
        const sel1 = row1 && Array.isArray(selectedItems) && selectedItems.includes(row1.id) ? 1 : 0;
        const sel2 = row2 && Array.isArray(selectedItems) && selectedItems.includes(row2.id) ? 1 : 0;
        return sel2 - sel1;
      })
      .map((row, index) => (
        <tr
          key={rowKey ? row[rowKey] : index}
          onClick={(e) => {
            if (multipleSelect && preventRedirectOnClick) {
              return this.toggleSelect(row, e);
            }

            if (preventRedirectOnClick) {
              return this.toggleSelect(row, e);
            }

            if (!row.demoData) {
              rowClick(row.id, e);
            }
          }}
          className={classNames('data-table__row', rowClassName, {
            isActive: this.state.settingSelected === row.id,
            'disabled-event': ((isRolePage && (row.name !== 'Owner' && (row.name !== 'Viso Support')) && disabled) || (!isRolePage && disabled)),
            'demo-data-event': row.demoData,
            'data-table__row_disabled': isSelectable && this.isDisabled(row),
            'data-table__row_selected': isSelectable && this.isSelected(row),
            [`data-table__row_${this.props.colorClassName}`]: this.props
              .colorClassName,
          })}
        >
          {isUserSetting && (
            <td className="data-table__td">
              <div className={`checkbox checkbox_${this.props.colorClassName}`}>
                <input
                  type="checkbox"
                  className="checkbox__input"
                  checked={this.isSelected(row)}
                  readOnly
                  onClick={(e) => {
                    e.stopPropagation();
                    this.setState({ isFirstTime: false });
                    this.toggleSelect(row, e);
                  }}
                />
                <div className="checkbox__image">
                  {getInitials(row.firstName, row.lastName)}
                </div>
              </div>
            </td>
          )}

          {isPriceSetting && (
            <>
              <td className="data-table__td">
                <div
                  className={`checkbox checkbox_${this.props.colorClassName}`}
                >
                  <input
                    type="checkbox"
                    className="checkbox__input"
                    checked={this.isSelected(row)}
                    readOnly
                    onClick={(e) => {
                      e.stopPropagation();
                      this.setState({ isFirstTime: false });
                      this.toggleSelect(row, e);
                    }}
                  />
                  <div className="checkbox__image">
                    {this.props.getCurrencyIcon[row.currency]
                      || row.currency.toUpperCase()}
                  </div>
                </div>
                {monthlyPriceId === row.id && (
                  <span
                    className={`data-cards__selected table-icon_ticker ${this.props.colorClassName}`}
                  >
                    <TickIcon />
                  </span>
                )}
                {yearlyPriceId === row.id && (
                  <span
                    className={`data-cards__selected table-icon_ticker ${this.props.colorClassName}`}
                  >
                    <TickIcon />
                  </span>
                )}
              </td>
            </>
          )}

          {!isNodeType && !isLogoSetting && !isUserSetting && isSelectable && !isPriceSetting && (
            <td className="data-table__td">
              <div
                className={classNames(
                  'checkbox',
                  this.props.checkboxClassName,
                  {
                    [`checkbox_${this.props.colorClassName}`]: this.props
                      .colorClassName,
                  },
                )}
                onClick={(ev) => {
                  this.setState({ isFirstTime: false });
                  ev.stopPropagation();
                  this.toggleSelect(row, ev);
                }}
              >
                {inputType === 'radio' ? (
                  <div className="radio">
                    <input
                      type="radio"
                      name="row"
                      className="radio__input"
                      checked={this.isSelected(row)}
                    />
                    <div className="radio__check" />
                  </div>
                ) : (
                  <>
                    <input
                      checked={this.isSelected(row)}
                      type="checkbox"
                      className="checkbox__input"
                      readOnly
                    />
                    {(row.isNew || row.isNewTemplate) && <div className="card__new"><FormattedMessage {...messages.new} /></div>}
                    {(isChecked && !row.isNewTemplate) && (
                      <span
                        className={`data-cards__plus ${this.props.colorClassName
                          }`}
                      >
                        <CirclePlusIcon />
                      </span>
                    )}
                    <div className={classNames('checkbox__image', {
                      'no-border-only': hasImg,
                      // 'checkbox__autoCheck_accent0': row.hasError
                    })}>
                      {hasImg ? (
                        this.props.CheckboxImage
                      ) : (
                        row.hasError ?
                          <VersionUpdateIcon />
                          :
                          <this.props.CheckboxImage row={row} />
                      )}
                    </div>

                  </>
                )}
              </div>
              {(row.healthCheck === false && row.status !== 'os_updating') || row.hasError && (
                <span
                  className={`data-cards__selected ${this.props.colorClassName} table-icon_ticker failure`}
                >
                  <IssueIcon />
                </span>
              )}
              {row.status === 'os_updating' && (
                <span
                  className={`data-cards__selected ${this.props.colorClassName} table-icon_ticker animate-rotation failure`}
                >
                  <ProgressIcon />
                </span>
              )}
            </td>
          )}

          {isLogoSetting && (
            <td className="data-table__td">
              <div className={`checkbox checkbox_${this.props.colorClassName}`}>
                <input
                  type="checkbox"
                  className="checkbox__input"
                  checked={this.isSelected(row)}
                  readOnly
                  onClick={(e) => {
                    e.stopPropagation();
                    this.setState({ isFirstTime: false });
                    this.toggleSelect(row, e);
                  }}
                />
                <div className={`${hasImg && 'no-border-only'} checkbox__image`}>
                  <img src={row.logo || 'http://placehold.it/88x88'} alt="Img" width="52px" height="52px" />
                </div>
              </div>
            </td>
          )}

          {isNodeType && (
            <td className="data-table__td">
              <div className={`checkbox checkbox_${this.props.colorClassName} checkbox-node-accent3`}>
                <input
                  type="checkbox"
                  className="checkbox__input"
                  checked={this.isSelected(row)}
                  readOnly
                  onClick={(e) => {
                    e.stopPropagation();
                    this.setState({ isFirstTime: false });
                    this.toggleSelect(row, e);
                  }}
                />
                <div className={`${hasImg && 'no-border-only'} checkbox__image checkbox-node`}>
                  <i className={objectIcon[row.type] || nodeIcon} width="52px" height="52px"></i>
                </div>
                {row.configModified &&
                  <span
                    className={`table-icon_node ${this.props.colorClassName}`}
                  >
                    <Circle
                      size={2}
                      fill="#FFF"
                      stroke="#FF7A7A"
                    />
                  </span>
                }
              </div>
            </td>
          )}

          <RowComponent
            {...row}
            {...this.props}
            rows={safeRows}
            settingSelected={this.state.settingSelected}
            onSelectSetting={this.onSelectSetting}
            onClearSelectedSetting={this.onClearSelectedSetting}
            hideSingleMenu={hideSingleMenu}
            onShowDeleteModal={onShowDeleteModal}
            {...this.props.rowProps}
            onPushProfile={onPushProfile}
            onAssignProfile={onAssignProfile}
            columns={this.state.columns}
            colorClassName={this.props.colorClassName}
            index={index}
          />
        </tr>
      ));
  }

  renderLoading() {
    const {
      rowClassName,
    } = this.props;

    return [1, 2, 3, 4, 5, 6, 7, 8]
      .map((row, index) => (
        <tr
          key={index}
          className={classNames('data-table__row', rowClassName, {
          })}
        >
          <td className="data-table__td">
            <div className={`checkbox checkbox_${this.props.colorClassName}`}>
              <input
                type="checkbox"
                className="checkbox__input"
                checked={this.isSelected(row)}
                readOnly
                onClick={(e) => {
                  e.stopPropagation();
                  this.setState({ isFirstTime: false });
                }}
              />
              <div className="checkbox__image">
              </div>
            </div>
          </td>
          <td className={classNames('data-table__td')} />
          <td className={classNames('data-table__td')} />
          <td className={classNames('data-table__td')} />
          <td className={classNames('data-table__td')} />
          <td className={classNames('data-table__td')} />
          <td className={classNames('data-table__td')} />
        </tr>
      ));
  }

  onColumnsChange = (columns) => {
    this.setState({ columns });
    
    // Save column state to localStorage if tableId is provided
    if (this.props.tableId) {
      saveColumnState(this.props.tableId, columns);
    }
  };

  renderEmptyComponent = () => {
    if (this.props.EmptyRowComponent) {
      return this.props.EmptyRowComponent();
    }

    return (
      <NotFound
        parentClass="no-result__container"
        color={this.props.colorClassName}
        strokeWidth="1"
        logo={this.props.notFoundIcon}
        title={<FormattedMessage {...messages.noResultsFound} />}
        description={<FormattedMessage {...messages.tryAdjustingSearch} />}
      />
    );
  };

  renderTableBody = () => {
    const { allowOnlySingleSelect, inputType } = this.props;

    return (
      <>
        <table
          className={classNames('data-table__table', this.props.tableClassName)}
        >
          {this.props.columnsSubHead && <TableSubHead
            inputType={this.props.inputType}
            onUpdateSort={this.props.onUpdateSort}
            currentSortDir={this.props.currentSortDir}
            currentSortKey={this.props.currentSortKey}
            columns={this.props.isLoading
              ?
              [
                { isVisible: true },
                { isVisible: true },
                { isVisible: true },
                { isVisible: true },
                { isVisible: true },
              ]
              :
              this.props.columnsSubHead}
            isSelectable={this.props.isSelectable}
            isSelectedAll={
              this.props.isSelectable ? this.isSelectedAll() : false
            }
            selectAll={this.selectAll}
            hideCheckbox={allowOnlySingleSelect || inputType === 'radio'}
            colorClassName={this.props.colorClassName}
            withColumnSelect={this.props.withColumnSelect}
            onColumnsChange={this.onColumnsChange}
          />
          }
          <TableHead
            hasSubHead={this.props.columnsSubHead}
            inputType={this.props.inputType}
            onUpdateSort={this.props.onUpdateSort}
            currentSortDir={this.props.currentSortDir}
            currentSortKey={this.props.currentSortKey}
            columns={this.props.isLoading
              ?
              [
                { isVisible: true },
                { isVisible: true },
                { isVisible: true },
                { isVisible: true },
                { isVisible: true },
              ]
              :
              this
                .state.columns}
            isSelectable={this.props.isSelectable}
            isSelectedAll={
              this.props.isSelectable ? this.isSelectedAll() : false
            }
            selectAll={this.selectAll}
            hideCheckbox={allowOnlySingleSelect || inputType === 'radio'}
            colorClassName={this.props.colorClassName}
            withColumnSelect={this.props.withColumnSelect}
            onColumnsChange={this.onColumnsChange}
          />
          <tbody className="data-table__body">
            {!!this.props.rows.length && !this.props.isLoading && this.renderRows()}
            {this.props.isLoading && this.renderLoading()}
            {!this.props.rows.length && !this.props.isLoading && (
              <tr className="data-table__row">
                <td className="data-table__td_empty" />
              </tr>
            )}
          </tbody>
        </table>
        {!this.props.rows.length
          && !this.props.isLoading
          && this.renderEmptyComponent()}
      </>
    );
  };

  isSelectedAll = () => {
    const { rows = [], selectedItems = [], disabledIds = [] } = this.props;

    // Never show as checked on initial render to avoid default-checked state
    if (this.state.isFirstTime) {
      return false;
    }

    // No rows => never show header checkbox as checked
    if (!rows.length) {
      return false;
    }

    // Consider only selectable rows on the current page
    const selectableRowIds = (rows ?? [])
      .filter(row => row && row.id != null && !disabledIds.includes(row.id))
      .map(row => row.id);

    // If there are no selectable rows, do not mark as selected all by default
    if (!selectableRowIds.length) {
      return false;
    }

    // Header is checked only if every selectable row is selected
    return selectableRowIds.every((id) => selectedItems.includes(id));
  };

  isSelected = (row) => {
    const { selectedItems = [] } = this.props;
    if (this.state.isFirstTime) {
      // this.setState({ isFirstTime: false });
    }

    return selectedItems.indexOf(row.id) > -1;
  };

  isDisabled = (row) => {
    if (row) {
      const { disabledIds = [] } = this.props;

      return disabledIds && disabledIds.indexOf(row.id) > -1;
    }
  };

  selectAll = (e, action) => {
    const { disabledIds = [] } = this.props;
    this.setState({ isFirstTime: false });

    if (this.isSelectedAll() || action === 'clearAll') {
      this.props.onDeselectAll && this.props.onDeselectAll();
      return this.props.onSelect([], e);
    }

    const allSelected = (this.props.rows || [])
      .filter(row => row && !disabledIds.includes(row.id))
      .map(row => row.id);

    this.props.onSelect(allSelected, e);
    this.props.onSelectAll && this.props.onSelectAll();
  };

  toggleSelect = (row, e) => {
    const {
      onSelect,
      selectedItems = [],
      inputType,
      allowOnlySingleSelect,
    } = this.props;

    this.setState({ isFirstTime: false });

    if (this.isDisabled(row)) {
      return false;
    }

    if (allowOnlySingleSelect) {
      return onSelect(row.id, e);
    }
    if (inputType === 'radio') {
      return onSelect(row.id, e);
    }
    if (this.isSelected(row)) {
      onSelect(selectedItems.filter(item => item !== row.id), e);
    } else {
      onSelect(selectedItems.concat(row.id), e);
    }
  };

  render() {
    // if (!this.props.hideLoader && this.props.isLoading) {
    //   return (
    //     <LoadingIndicator
    //       customClass="loading-table"
    //       color={this.props.colorClassName}
    //       isProcessPage={this.props.isProcessPage}
    //     />
    //   );
    // }

    if (this.props.customScrollbar) {
      return (
        <Scrollbars
          autoHeight
          style={{ minHeight: '250px', maxHeight: 'auto' }}
          renderTrackHorizontal={props => <div {...props} className="track-horizontal" />}
          autoHideDuration={1}
          renderView={({ style, ...props }) => (
            <div
              {...props}
              style={{
                ...style,
                maxHeight: 'auto',
                overflowY: 'hidden', // Control vertical scrolling
              }}
            />
          )}
        >
          <div className={classNames('', {
            'data-table__loading': !this.props.hideLoader && this.props.isLoading,
            'data-table': !this.props.isLoading,
          })}
          >
            <div className="content-2">
              {this.renderTableBody()}
            </div>
          </div>
        </Scrollbars>
      );
    }

    return (
      <div className={classNames('', {
        'data-table__loading': !this.props.hideLoader && this.props.isLoading,
        'data-table': !this.props.isLoading,
      })}
      >

        {this.renderTableBody()}
      </div>
    );
  }
}

Table.propTypes = {
  isUserSetting: PropTypes.bool,
  inputType: PropTypes.string,
  columns: PropTypes.array.isRequired,
  currentSortKey: PropTypes.string,
  currentSortDir: PropTypes.string,
  EmptyRowComponent: PropTypes.func,
  RowComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  CheckboxImage: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  rowClassName: PropTypes.string,
  checkboxClassName: PropTypes.string,
  rowClick: PropTypes.func,
  rowKey: PropTypes.string,
  rows: PropTypes.array.isRequired,
  rowProps: PropTypes.object,
  tableClassName: PropTypes.string,
  onUpdateSort: PropTypes.func,
  isSelectable: PropTypes.bool,
  selectedItems: PropTypes.array,
  onSelect: PropTypes.func,
  onPushProfile: PropTypes.func,
  onAssignProfile: PropTypes.func,
  selectAllClass: PropTypes.string,
  colorClassName: PropTypes.string,
  withColumnSelect: PropTypes.bool,
};

Table.defaultProps = {
  onUpdateSort: f => f,
  rowClick: f => f,
};

const mapStateToProps = createStructuredSelector({
  me: makeSelectMe(),
});

const withConnect = connect(
  mapStateToProps,
  null,
);

export default compose(
  withConnect,
)(Table);
